/**
 * Pure layout function: takes the server-side WorkflowGraph wire shape and
 * runs dagre over it to assign x/y coordinates, then returns React Flow's
 * Node[] / Edge[] tuple. Renderer-agnostic except for the React Flow
 * id/type/position field names — see Section 5 of the redesign doc.
 *
 * Layout config matches the spec verbatim:
 *   rankdir 'TB' (top → bottom), nodesep 40, ranksep 60.
 *
 * Node sizes here are a coarse default; React Flow will measure the actual
 * DOM after the first render and the layout reflows on the next call.
 * We size triggers a hair smaller than steps, and end nodes smaller still.
 */

import * as dagre from '@dagrejs/dagre';
import { MarkerType, type Edge as RfEdge, type Node as RfNode } from '@xyflow/react';
import type {
  ActionSpec,
  ConditionSpec,
  StepCategory,
  TriggerSpec,
  WfNode,
  WorkflowGraph,
} from '@mana/shared';

// ---------------------------------------------------------------------------
// React-Flow-friendly data shapes. React Flow's Node<TData, TType> generic
// requires TData to extend Record<string, unknown>; we mirror the WfNode
// payloads field-by-field so the consumer can still narrow on .kind.
// ---------------------------------------------------------------------------

export interface TriggerNodeData extends Record<string, unknown> {
  kind: 'trigger';
  trigger: TriggerSpec;
  label: string;
}

export interface StepNodeData extends Record<string, unknown> {
  kind: 'step';
  stepKey: string;
  label: string;
  category: StepCategory;
  actions: ActionSpec[];
  guard?: ConditionSpec;
  terminal?: boolean;
}

export interface EndNodeData extends Record<string, unknown> {
  kind: 'end';
  label: string;
  outcome: 'completed' | 'cancelled' | 'errored';
}

export type TriggerRfNode = RfNode<TriggerNodeData, 'trigger'>;
export type StepRfNode = RfNode<StepNodeData, 'step'>;
export type EndRfNode = RfNode<EndNodeData, 'end'>;
export type WorkflowRfNode = TriggerRfNode | StepRfNode | EndRfNode;

// React Flow's Edge wants TData : Record<string, unknown>. We don't ship
// per-edge data into the renderer — the layout only needs the topology — so
// we leave edges as the default Edge<Record<string, unknown> | undefined>.
export type WorkflowRfEdge = RfEdge;

export interface LaidOutGraph {
  nodes: WorkflowRfNode[];
  edges: WorkflowRfEdge[];
}

// ---------------------------------------------------------------------------
// Sizes used to seed dagre. Real DOM is rendered after layout, so these
// only need to be reasonable approximations of the final node footprint.
// ---------------------------------------------------------------------------

const SIZE_TRIGGER = { w: 220, h: 64 } as const;
const SIZE_STEP = { w: 260, h: 92 } as const;
const SIZE_END = { w: 140, h: 44 } as const;

function sizeForKind(kind: WfNode['kind']): { w: number; h: number } {
  switch (kind) {
    case 'trigger':
      return SIZE_TRIGGER;
    case 'step':
      return SIZE_STEP;
    case 'end':
      return SIZE_END;
  }
}

// ---------------------------------------------------------------------------
// Convert a server WfNode into the matching React Flow data payload.
// ---------------------------------------------------------------------------

function wfNodeToData(
  node: WfNode,
): TriggerNodeData | StepNodeData | EndNodeData {
  switch (node.kind) {
    case 'trigger':
      return { kind: 'trigger', trigger: node.trigger, label: node.label };
    case 'step':
      return {
        kind: 'step',
        stepKey: node.stepKey,
        label: node.label,
        category: node.category,
        actions: node.actions,
        ...(node.guard ? { guard: node.guard } : {}),
        ...(node.terminal ? { terminal: node.terminal } : {}),
      };
    case 'end':
      return { kind: 'end', label: node.label, outcome: node.outcome };
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function layoutWorkflowGraph(graph: WorkflowGraph): LaidOutGraph {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'TB', nodesep: 40, ranksep: 60, marginx: 20, marginy: 20 });
  // dagre demands an edge label default even if we don't use it.
  g.setDefaultEdgeLabel(() => ({}));

  // Seed dagre with node sizes.
  for (const node of graph.nodes) {
    const { w, h } = sizeForKind(node.kind);
    g.setNode(node.id, { width: w, height: h });
  }
  // Filter edges to only those that connect known nodes so dagre doesn't trip.
  const nodeIds = new Set(graph.nodes.map((n) => n.id));
  const usableEdges = graph.edges.filter(
    (e) => nodeIds.has(e.source) && nodeIds.has(e.target),
  );
  for (const e of usableEdges) {
    g.setEdge(e.source, e.target);
  }

  dagre.layout(g);

  // ---- map nodes back to React Flow shape -------------------------------
  const rfNodes: WorkflowRfNode[] = graph.nodes.map((n): WorkflowRfNode => {
    const dn = g.node(n.id);
    const { w, h } = sizeForKind(n.kind);
    // dagre reports the centre of the node; React Flow wants the top-left.
    const x = (dn?.x ?? 0) - w / 2;
    const y = (dn?.y ?? 0) - h / 2;
    const data = wfNodeToData(n);
    if (data.kind === 'trigger') {
      const out: TriggerRfNode = {
        id: n.id,
        type: 'trigger',
        position: { x, y },
        data,
        draggable: false,
        selectable: true,
      };
      return out;
    }
    if (data.kind === 'step') {
      const out: StepRfNode = {
        id: n.id,
        type: 'step',
        position: { x, y },
        data,
        draggable: false,
        selectable: true,
      };
      return out;
    }
    const out: EndRfNode = {
      id: n.id,
      type: 'end',
      position: { x, y },
      data,
      draggable: false,
      selectable: false,
    };
    return out;
  });

  // ---- map edges --------------------------------------------------------
  const rfEdges: WorkflowRfEdge[] = usableEdges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    type: 'smoothstep',
    ...(e.label ? { label: e.label } : {}),
    animated: false,
    style: {
      stroke: '#1B3A5C',
      strokeWidth: 1.5,
      strokeOpacity: 0.8,
      ...(e.kind === 'guarded' ? { strokeDasharray: '6 4' } : {}),
    },
    labelStyle: {
      fill: '#1B3A5C',
      fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
      fontSize: 11,
      fontWeight: 500,
    },
    labelBgStyle: {
      fill: '#F2F5F9',
      stroke: '#BFCCDC',
      strokeWidth: 1,
    },
    labelBgPadding: [4, 2],
    labelBgBorderRadius: 4,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: '#1B3A5C',
      width: 16,
      height: 16,
    },
  }));

  return { nodes: rfNodes, edges: rfEdges };
}
